import { type InputCapture } from '@canvas/input';
import type {
  PointerDownCapture,
  PointerMoveCapture,
  PointerUpCapture,
} from '@canvas/input/types/pointer';

import { type SelectionsMachine } from '../../../machine/selection-machine';
import { type SelectionState } from '../../../machine/state/types';
import { isCellSelected } from '../../../machine/utils/is-cell-selected';
import { isRowIndexSelectedInRowMode } from '../../../machine/utils/is-row-index-selected-in-row-mode';
import { isSameCell } from '../../../machine/utils/is-same-cell';
import { sumSelectedRowSpan } from '../../../machine/utils/sum-selected-row-span';
import { type GridPosition } from '../../../selections';
import { type SelectionUiInteraction } from '../selection-ui-interaction';
import { type AutoScroller, createAutoScroller } from '../utils/create-auto-scroller';
import { type ClickTarget } from '../utils/get-click-target';

import { dispatchInitialClick } from './dispatch-initial-click';

export interface CreateMouseStateMachineParams {
  machine: SelectionsMachine;
  inputCapture: InputCapture;
  getClickTarget: (x: number, y: number) => ClickTarget;
  getScrollContainer: () => HTMLElement | null;
  getMaxVisibleRowIndex: () => number;
  getVisibleEntryCount: () => number;
  /**
   * When set, reports pointer-driven selection UI interactions (cell, row checkbox,
   * grid header checkbox) for downstream metrics; emitters stay free of app scope.
   */
  onSelectionUiInteraction?: (event: SelectionUiInteraction) => void;
}

export interface MouseState {
  onPointerDown(event: PointerDownCapture): MouseState;
  onPointerMove(event: PointerMoveCapture): MouseState;
  onPointerUp(event: PointerUpCapture): MouseState;
}

interface MachineContext {
  /**
   * The grid position of the cell where the initial pointerdown event occurred.
   * Used to determine if the pointer was released on the same cell.
   */
  pointerDownCell: GridPosition | null;

  /**
   * Indicates whether the cell was already part of the active selection
   * before the current pointer interaction began.
   */
  wasAlreadySelected: boolean;

  /**
   * Flags whether the pointer has moved to a different cell during a drag interaction.
   * Prevents accidental static clicks when the user intended to drag.
   */
  hasDragged: boolean;

  /**
   * Tracks the most recent valid cell position during a fill drag operation.
   * This allows the fill to complete even when the pointer is released outside
   * the grid bounds (e.g., when the user drags beyond the table edge). Reset to
   * `null` when a new fill operation starts and after the fill pointer interaction ends.
   */
  lastValidFillCell: GridPosition | null;

  /**
   * Used to force auto scrolling when at the edges of the inner container.
   */
  autoScroller: AutoScroller | null;

  /**
   * Grid indices for a pending static cell selection metric, set on pointer down when
   * the user may commit a new cell selection (not already selected, no shift).
   */
  pendingCellSelectionMetricIndices: { rowIndex: number; colIndex: number } | null;

  /**
   * `performance.now()` captured in `dispatchInitialClick` immediately before `selectCell`
   * when selection UI telemetry is enabled. Used as `startTime` for
   * `selectionCommitted` on pointer-up (dispatch moment, not release).
   */
  pendingCellSelectionMetricStartTimeMs: number | null;

  /**
   * Resets all context values to their initial idle state and safely cleans up
   * any active auto-scroller.
   */
  reset: VoidFunction;
}

function createMachineContext(): MachineContext {
  return {
    pointerDownCell: null,
    wasAlreadySelected: false,
    hasDragged: false,
    lastValidFillCell: null,
    autoScroller: null,
    pendingCellSelectionMetricIndices: null,
    pendingCellSelectionMetricStartTimeMs: null,

    reset() {
      // Clean up the active scroller before nullifying
      this.autoScroller?.stop();
      this.autoScroller = null;

      this.pointerDownCell = null;
      this.wasAlreadySelected = false;
      this.hasDragged = false;
      this.lastValidFillCell = null;
      this.pendingCellSelectionMetricIndices = null;
      this.pendingCellSelectionMetricStartTimeMs = null;
    },
  };
}

export function createMouseStateMachine(
  params: CreateMouseStateMachineParams
): MouseState {
  const {
    machine,
    inputCapture,
    getClickTarget,
    getScrollContainer,
    getMaxVisibleRowIndex,
    getVisibleEntryCount,
    onSelectionUiInteraction,
  } = params;

  const context = createMachineContext();

  // --- Helpers ---

  function getPointerModifiers() {
    return {
      shift: inputCapture.hasKeyModifierPressed('Shift'),
      metaOrCtrl:
        inputCapture.hasKeyModifierPressed('Meta') ||
        inputCapture.hasKeyModifierPressed('Control'),
    };
  }

  function emitCellPointer(args: {
    phase: 'selectionCommitted' | 'editRequested';
    startTime: number;
    rowIndex: number;
    colIndex: number;
  }) {
    onSelectionUiInteraction?.({
      source: 'cellPointer',
      startTime: args.startTime,
      modifiers: getPointerModifiers(),
      detail: {
        phase: args.phase,
        rowIndex: args.rowIndex,
        colIndex: args.colIndex,
      },
    });
  }

  function emitCheckboxMetrics(
    target: ClickTarget,
    stateBefore: SelectionState,
    startTime: number
  ) {
    if (!onSelectionUiInteraction) {
      return;
    }

    if (target.type === 'row' && target.source === 'row-checkbox') {
      onSelectionUiInteraction({
        source: 'rowCheckbox',
        startTime,
        modifiers: getPointerModifiers(),
        detail: {
          rowIndex: target.row.index,
          expectRowCheckboxChecked: isRowIndexSelectedInRowMode(
            machine.getState(),
            target.row.index
          ),
        },
      });
    }

    if (target.type === 'headerCheckbox') {
      const visibleEntryCount = getVisibleEntryCount();

      if (visibleEntryCount === 0) {
        return;
      }

      const fullySelected =
        stateBefore.mode === 'row' &&
        sumSelectedRowSpan(stateBefore) === visibleEntryCount;

      onSelectionUiInteraction({
        source: 'headerCheckbox',
        startTime,
        modifiers: { shift: false, metaOrCtrl: false },
        detail: {
          checkboxState: fullySelected ? 'unselected' : 'selected',
          expectChecked: !fullySelected,
        },
      });
    }
  }

  function updateAutoScroller(
    x: number,
    y: number,
    extendFn: (px: number, py: number) => void
  ) {
    if (!context.autoScroller) {
      context.autoScroller = createAutoScroller({ getScrollContainer }, extendFn);
    }

    context.autoScroller.update(x, y);
  }

  function extendDragToCell(x: number, y: number) {
    const target = getClickTarget(x, y);

    if (target.type === 'cell') {
      machine.dispatch({
        type: 'extendToCell',
        cell: target.position,
        source: 'mouse',
        isDragging: true,
      });
    }
  }

  function extendFillToCell(x: number, y: number) {
    const target = getClickTarget(x, y);

    if (target.type === 'cell') {
      context.lastValidFillCell = target.position;

      machine.dispatch({
        type: 'fillDrag',
        cell: target.position,
        source: 'mouse',
      });
    }
  }

  // --- State Machine ---

  const idleState: MouseState = {
    // There's no way to get a pointerup or pointermove
    // event in the idle state, so we just fill these with idleState
    onPointerUp() {
      context.reset();

      return idleState;
    },

    onPointerMove() {
      return idleState;
    },

    onPointerDown(event) {
      const { x, y, button } = event.properties;

      // if the button is secondary, that means right-click, which is also context-menu
      // so we ignore it here.
      // this avoids a right click conflicting with a context menu
      if (button === 'secondary') {
        return idleState;
      }

      const target = getClickTarget(x, y);

      if (target.type === 'none') {
        return idleState;
      }

      const stateBefore = machine.getState();

      if (target.type === 'cell') {
        context.pointerDownCell = target.position;
        context.hasDragged = false;
        context.wasAlreadySelected = isCellSelected(stateBefore, target.position);
      } else {
        context.pointerDownCell = null;
        context.wasAlreadySelected = false;
      }

      /**
       * If we're editing currently, we may have to ignore, intercept, OR fire a stopEditing dispatch.
       */
      if (stateBefore.isEditing) {
        if (
          target.type === 'cell' &&
          stateBefore.focusedCell &&
          isSameCell(stateBefore.focusedCell, target.position)
        ) {
          // User clicked INSIDE the active editor (e.g., to move the text cursor).
          // We swallow the event completely. Do not dispatch selection events,
          // and strictly return idleState so we don't start dragging.
          return idleState;
        }

        // User clicked ELSEWHERE (a different cell, row header, etc.)
        // We gracefully commit the active edit before we let the selection change.
        machine.dispatch({ type: 'stopEditing', source: 'mouse' });
      }

      window.getSelection()?.removeAllRanges();

      if (target.type === 'outside') {
        machine.dispatch({ type: 'deselectAll', source: 'mouse' });

        // in case we were editing, we want to make sure the state is reset
        machine.dispatch({ type: 'stopEditing', source: 'mouse' });

        return idleState;
      }

      if (target.type === 'fillHandle') {
        context.lastValidFillCell = null;

        machine.dispatch({ type: 'fillStart', source: 'mouse' });

        return fillingState;
      }

      const { shift, metaOrCtrl } = getPointerModifiers();
      const clickStartTime = performance.now(); // Grab the time once

      dispatchInitialClick(machine, target, {
        extend: shift,
        toggle: metaOrCtrl,
        getVisibleEntryCount,
        getMaxVisibleRowIndex,
        ...(onSelectionUiInteraction && {
          onSimpleCellSelectDispatch(startTime: number) {
            context.pendingCellSelectionMetricStartTimeMs = startTime;
          },
        }),
      });

      // All metrics logic hidden inside this helper
      emitCheckboxMetrics(target, stateBefore, clickStartTime);

      // if we're in a pointerdown state and we're not shift
      // clicking, we can transition to a dragging state
      if (target.type === 'cell' && !shift) {
        context.pendingCellSelectionMetricIndices = context.wasAlreadySelected
          ? null
          : {
              rowIndex: target.position.row.index,
              colIndex: target.position.col.index,
            };

        return draggingState;
      }

      return idleState;
    },
  };

  const draggingState: MouseState = {
    // If the pointer is down and we continue to get
    // pointerdown events, we ignore them
    onPointerDown() {
      return draggingState;
    },

    onPointerMove(event) {
      const { x, y } = event.properties;
      const target = getClickTarget(x, y);

      if (
        target.type === 'cell' &&
        context.pointerDownCell &&
        !isSameCell(context.pointerDownCell, target.position)
      ) {
        context.hasDragged = true;
      }

      extendDragToCell(x, y);
      updateAutoScroller(x, y, extendDragToCell);

      return draggingState;
    },
    onPointerUp(event) {
      const { x, y } = event.properties;
      const target = getClickTarget(x, y);

      if (target.type === 'cell') {
        const isStaticClick =
          context.pointerDownCell &&
          isSameCell(context.pointerDownCell, target.position) &&
          !context.hasDragged;

        const pointerUpTimeMs = performance.now();

        if (isStaticClick && context.pendingCellSelectionMetricIndices) {
          emitCellPointer({
            phase: 'selectionCommitted',
            startTime: context.pendingCellSelectionMetricStartTimeMs ?? pointerUpTimeMs,
            rowIndex: context.pendingCellSelectionMetricIndices.rowIndex,
            colIndex: context.pendingCellSelectionMetricIndices.colIndex,
          });
        }

        if (isStaticClick && context.wasAlreadySelected) {
          machine.dispatch({
            type: 'startEditing',
            cell: target.position,
            source: 'mouse',
          });

          emitCellPointer({
            phase: 'editRequested',
            startTime: pointerUpTimeMs,
            rowIndex: target.position.row.index,
            colIndex: target.position.col.index,
          });
        } else if (context.hasDragged) {
          // Finalize the selection for non-static-click scenarios
          // (i.e. the user actually dragged to a new cell).
          machine.dispatch({
            type: 'extendToCell',
            cell: target.position,
            source: 'mouse',
          });
        }
      }

      context.reset();

      return idleState;
    },
  };

  const fillingState: MouseState = {
    onPointerDown() {
      return fillingState;
    },

    onPointerMove(event) {
      const { x, y } = event.properties;

      extendFillToCell(x, y);
      updateAutoScroller(x, y, extendFillToCell);

      return fillingState;
    },

    onPointerUp(event) {
      const { x, y } = event.properties;

      extendFillToCell(x, y);

      if (context.lastValidFillCell) {
        machine.dispatch({
          type: 'fillApply',
          cell: context.lastValidFillCell,
          source: 'mouse',
        });
      }

      machine.dispatch({ type: 'fillEnd', source: 'mouse' });

      context.reset();

      return idleState;
    },
  };

  return idleState;
}
